<template>
  <basic-card>
    <template slot="header">{{ internalDefinition.name }} {{ internalDefinition.id ? '' : `(${$t('unsaved')})` }}</template>
    <form-wrapper>
      <form-text-input :caption="$t('cashflow.definition.name')" v-model="internalDefinition.name"/>
      <form-text-input :caption="$t('cashflow.definition.order')" v-model="internalDefinition.order"/>
      <form-expression-date-input :caption="$t('cashflow.definition.startDateExpression')" v-model="internalDefinition.startDateExpression"
                                  help-item-code="cash-flow-start-date"/>
      <form-expression-date-input :caption="$t('cashflow.definition.endDateExpression')" v-model="internalDefinition.endDateExpression"
                                  help-item-code="cash-flow-end-date"/>
      <form-expression-date-input :caption="$t('cashflow.definition.breakDateExpression')" v-model="internalDefinition.breakDateExpression"
                                  help-item-code="cash-flow-break-date"/>
      <form-expression-date-input :caption="$t('cashflow.definition.startMonthlyBucketsExpression')" v-model="internalDefinition.startMonthlyBucketsExpression"
                                  help-item-code="cash-flow-start-monthly"/>
      <form-checkbox-input :caption="$t('cashflow.definition.convertToReportCurrency')"
                           help-item-code="cash-flow-convert-currency"
                           v-model="internalDefinition.convertToReportCurrency"/>
      <form-select-input :caption="$t('cashflow.definition.reportCurrency')" v-if="internalDefinition.convertToReportCurrency"
                         :custom-renderer="customRenderers.CurrencyVisualizer"
                         :options="currencyOptions" v-model="internalDefinition.reportCurrency"
                         help-item-code="cash-flow-report-currency"/>
      <form-checkbox-input :caption="$t('cashflow.definition.projectOperationsToBalance')"
                           help-item-code="cash-flow-project-balances"
                           v-model="internalDefinition.projectOperationsToBalances"/>
      <form-checkbox-input :caption="$t('cashflow.definition.useBreakDateForProjection')"
                           help-item-code="cash-flow-use-break-date"
                           v-model="internalDefinition.useBreakDateForProjection"/>
      <form-select-input :caption="$t('cashflow.definition.denominator')"
                         :options="denominationOptions" v-model="internalDefinition.denominator"
                         help-item-code="cash-flow-report-denominator"/>
      <form-select-input :caption="$t('cashflow.definition.shiftIncomeCashEvents')"
                         :options="shiftOptions" v-model="internalDefinition.shiftIncomeCashEvents"
                         help-item-code="cash-flow-report-shiftIncomeCashEvents"/>
      <form-select-input :caption="$t('cashflow.definition.shiftOutcomeCashEvents')"
                         :options="shiftOptions" v-model="internalDefinition.shiftOutcomeCashEvents"
                         help-item-code="cash-flow-report-shiftOutcomeCashEvent"/>
<!--      <form-checkbox-input caption="Live report"-->
<!--                           help-item-code="cash-flow-live-report"-->
<!--                           v-model="internalDefinition.liveReport"/>-->
<!--      <form-checkbox-input caption="Exclude internal movements"-->
<!--                           help-item-code="cash-flow-internal-movements"-->
<!--                           v-model="internalDefinition.excludeInternalBalanceMovements"/>-->
    </form-wrapper>

    <collapsible-content start-collapsed>
      <template slot="header">{{$t("cashflow.definition.cashEventColumnsSetup")}}
        <context-help help-item-code="cash-flow-cash-event-cols"/>
      </template>
      <div class="selection-grid">
        <div>
          <h1>{{$t("cashflow.definition.availableCashEventColumns")}}</h1>
          <div class="columns-container">
            <draggable :list="cashEventColumns" :group="{ name: 'columns' }" class="drop-area">
              <div class="columns-item" v-for="column in cashEventColumns">
                {{ cashEventColumnNames[column] }}
              </div>
            </draggable>
          </div>
        </div>
        <div class="selection-arrows">
          <simple-button
              @press="() => { cashEventColumns = [ ...cashEventColumns, ... internalDefinition.columns]; internalDefinition.columns = [] }">
            <icon :icon="['fal', 'chevron-double-left']"/>
          </simple-button>
          <br/>
          <simple-button
              @press="() => { internalDefinition.columns = [... internalDefinition.columns, ... cashEventColumns]; cashEventColumns = [];} ">
            <icon :icon="['fal', 'chevron-double-right']"/>
          </simple-button>
        </div>
        <div>
          <h1>{{$t("cashflow.definition.selectedCashEventColumns")}}</h1>
          <div class="columns-container">
            <draggable :list="internalDefinition.columns" group="columns" class="drop-area">
              <div class="columns-item" v-for="(column, idx) in internalDefinition.columns">
                {{ cashEventColumnNames[column] }}
              </div>
            </draggable>
          </div>
        </div>
      </div>

    </collapsible-content>

    <collapsible-content start-collapsed>
      <template slot="header">{{$t("cashflow.definition.balancesColumnsSetup")}}
        <context-help help-item-code="cash-flow-balance-cols"/>
      </template>
      <div class="selection-grid">
        <div>
          <h1>{{$t("cashflow.definition.availableBalanceColumns")}}</h1>
          <div class="columns-container">
            <draggable :list="balanceColumns" :group="{ name: 'balanceColumns' }" class="drop-area">
              <div class="columns-item" v-for="column in balanceColumns">{{ balanceColumnNames[column] }}
              </div>
            </draggable>
          </div>
        </div>
        <div class="selection-arrows">
          <simple-button
              @press="() => { balanceColumns = [ ...balanceColumns, ... internalDefinition.balanceColumns]; internalDefinition.balanceColumns = [] }">
            <icon :icon="['fal', 'chevron-double-left']"/>
          </simple-button>
          <br/>
          <simple-button
              @press="() => { internalDefinition.balanceColumns = [... internalDefinition.balanceColumns, ... balanceColumns]; balanceColumns = [];} ">
            <icon :icon="['fal', 'chevron-double-right']"/>
          </simple-button>
        </div>
        <div>
          <h1>{{$t("cashflow.definition.selectedBalanceColumns")}}</h1>
          <div class="columns-container">
            <draggable :list="internalDefinition.balanceColumns" group="balanceColumns" class="drop-area">
              <div class="columns-item" v-for="(column, idx) in internalDefinition.balanceColumns">
                {{ balanceColumnNames[column] }}
              </div>
            </draggable>
          </div>
        </div>
      </div>
    </collapsible-content>
    <collapsible-content start-collapsed>
      <template slot="header">{{$t("cashflow.definition.operationQueries")}}
        <context-help help-item-code="cash-flow-operation-queries"/>
      </template>
      <form-wrapper>
        <form-multi-select-input :caption="$t('cashflow.definition.operationQueries')"
                                 :options="operationQueryOptions.map(val => ({id: val.name, name: val.name}))"
                                 v-model="internalDefinition.savedQueryNames"/>
      </form-wrapper>
    </collapsible-content>

    <collapsible-content start-collapsed>
      <template slot="header">{{$t("cashflow.definition.cashEventQueries")}}
        <context-help help-item-code="cash-flow-cash-event-queries"/>
      </template>
      <form-wrapper>
        <form-multi-select-input :caption="$t('cashflow.definition.cashEventQueries')"
                                 :options="cashEventQueryOptions.map(val => ({id: val.name, name: val.name}))"
                                 v-model="internalDefinition.savedCashEventQueryNames"/>
        <form-multi-select-input :caption="$t('cashflow.definition.ignoreInBalancesQueries')"
                                 :options="cashEventQueryOptions.map(val => ({id: val.name, name: val.name}))"
                                 v-model="internalDefinition.ignoreInBalances"/>
      </form-wrapper>
    </collapsible-content>

    <collapsible-content start-collapsed>
      <template slot="header">{{$t("cashflow.definition.cashEventAdditionalFilters")}}
        <context-help help-item-code="cash-flow-cash-event-filters"/>
      </template>

      <form-wrapper>
        <form-multi-select-input :caption="$t('cashflow.definition.accounts')" :options="accountsForFilter"
                                 v-model="internalDefinition.cashflowFilter.criteriaMap.ACCOUNT"/>
        <form-multi-select-input :caption="$t('cashflow.definition.currencies')" :options="currencies"
                                 v-model="internalDefinition.cashflowFilter.criteriaMap.CURRENCY"/>
        <form-multi-select-input :caption="$t('cashflow.definition.accountOwners')" :options="partyOptions"
                                 v-model="internalDefinition.cashflowFilter.criteriaMap.ACCOUNT_OWNER"/>
        <form-multi-select-input :caption="$t('cashflow.definition.sources')"
                                 :options="cashEventFieldValues('source').map(val => ({id: val, name: val}))"
                                 v-model="internalDefinition.cashflowFilter.criteriaMap.EVENT_SOURCE"/>
        <form-multi-select-input :caption="$t('cashflow.definition.types')"
                                 :options="cashEventFieldValues('type').map(val => ({id: val, name: val}))"
                                 v-model="internalDefinition.cashflowFilter.criteriaMap.TYPE"/>
      </form-wrapper>
      <!--            <h2>Account</h2>-->
      <!--            <div>-->

      <!--                <select multiple v-model="internalDefinition.cashflowFilter.criteriaMap.ACCOUNT">-->
      <!--                    <option v-for="acc in accountsForFilter" :value="acc.id">{{acc.name}}</option>-->
      <!--                </select>-->
      <!--            </div>-->
      <!--            <h2>Currency</h2>-->
      <!--            <select multiple v-model="internalDefinition.cashflowFilter.criteriaMap.CURRENCY">-->
      <!--                <option v-for="ccy in currencies" :value="ccy.id">{{ccy.name}}</option>-->
      <!--            </select>-->
      <!--            <h2>Account owner</h2>-->
      <!--            <select multiple v-model="internalDefinition.cashflowFilter.criteriaMap.ACCOUNT_OWNER">-->
      <!--                <option v-for="party in partyOptions" :value="party.id">{{party.name}}</option>-->
      <!--            </select>-->
      <!--            <h2>Source</h2>-->
      <!--            <select multiple v-model="internalDefinition.cashflowFilter.criteriaMap.EVENT_SOURCE">-->
      <!--                <option v-for="src in cashEventFieldValues('source')" :value="src">{{src}}</option>-->
      <!--            </select>-->
      <!--            <h2>Type</h2>-->
      <!--            <select multiple v-model="internalDefinition.cashflowFilter.criteriaMap.EVENT_TYPE">-->
      <!--                <option v-for="type in cashEventFieldValues('type')" :value="type">{{type}}</option>-->
      <!--            </select>-->
    </collapsible-content>
    <collapsible-content>
      <template #header>
        {{$t("cashflow.definition.split")}}
      </template>
      <form-wrapper>
        <form-text-input v-model="internalDefinition.splitDateExpression" :caption="$t('cashflow.definition.splitDateExpression')"/>
        <form-text-input v-model="internalDefinition.splitCaption" :caption="$t('cashflow.definition.splitCaption')"/>
      </form-wrapper>
    </collapsible-content>
    <collapsible-content>
      <template #header>
        {{$t("cashflow.definition.effects")}}
      </template>
      <form-wrapper>
        <form-multi-select-input v-model="internalDefinition.cashEffects" :caption="$t('cashflow.definition.effects')" :options="cashEffectOptions"/>
      </form-wrapper>
    </collapsible-content>
    <!--        <collapsible-content>-->
    <!--            <template v-slot:header>-->
    <!--                Visibility-->
    <!--            </template>-->
    <!--            <form-wrapper>-->
    <!--                <form-multi-select-input caption="Users" :options="userOptions" v-model="internalDefinition.visibleToUsers"/>-->
    <!--            </form-wrapper>-->
    <!--        </collapsible-content>-->

    <simple-horizontal-layout>
      <empty/>
      <empty/>
      <simple-button @press="emitSave">{{$t("cashflow.definition.save")}}</simple-button>
    </simple-horizontal-layout>
  </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import SimpleHorizontalLayout from "../components/layout/SimpleHorizontalLayout";
import Empty from "../components/layout/Empty";
import SimpleButton from "../components/button/SimpleButton";
import FormCheckboxInput from "../components/form/FormCheckboxInput";
import FormTextInput from "../components/form/FormTextInput";
import FormSelectInput from "../components/form/FormSelectInput";
import {mapGetters} from "vuex";
import FormWrapper from "../components/form/FormWrapper";
import CurrencyVisualizer from "../components/selection-visualizers/CurrencyVisualizer";
import FormDateInput from "../components/form/FormDateInput";
import CollapsibleContent from "../components/CollapsibleContent";
import FormExpressionDateInput from "../components/form/FormDateExpressionInput";
import SimpleLayout from "../components/layout/SimpleLayout";
import FilterInputDate from "../components/filters/FilterInputDate";
import ContextHelp from "../components/help/ContextHelp";
import FilterInputSelection from "../components/filters/FilterInputSelection";
import FormMultiSelectInput from "../components/form/FormMultiSelectInput";

export default {
  name: "CashflowReportDefinitionCard",
  components: {
    FormMultiSelectInput,
    FilterInputSelection,
    ContextHelp,
    FilterInputDate,
    SimpleLayout,
    FormExpressionDateInput,
    CollapsibleContent,
    FormDateInput,
    FormWrapper,
    FormSelectInput,
    FormTextInput, FormCheckboxInput, SimpleButton, Empty, SimpleHorizontalLayout, BasicCard
  },
  props: {
    definition: Object
  },
  data: function () {
    return {
      denominationOptions: [
        {name: this.$t("ones"), id: 1},
        {name: this.$t("thousands"), id: 1000},
        {name: this.$t("millions"), id: 1000000}
      ],
      shiftOptions: [
        {name: this.$t("noShift"), id: "NO_SHIFT"},
        {name: this.$t("fridayShift"), id: "SHIFT_FRIDAY"},
        {name: this.$t("mondayShift"), id: "SHIFT_MONDAY"},
        {name: this.$t("nearShift"), id: "SHIFT_NEAR"},
      ],
      internalDefinition: (() => {
        let def = this.$_.cloneDeep(this.definition);
        if (typeof def.cashflowFilter == 'undefined') {
          def.cashflowFilter = {};
          def.cashflowFilter.criteriaMap = {
            ACCOUNT: [],
            CURRENCY: [],
            EVENT_SOURCE: [],
            EVENT_TYPE: [],
            ACCOUNT_OWNER: []
          };
        }
        if (def.savedQueryNames == undefined) {
          def.savedQueryNames = [];
        }
        if (def.savedCashEventQueryNames == undefined) {
          def.savedCashEventQueryNames = [];
        }
        return def;
      })(),
      balanceColumns: [
        "balance.currency",
        //"balance.bankCode",
        "balance.accountNumber",
        "balance.accountName",
        "balance.accountOwner",
        "balance.accountType"
      ],
      balanceColumnNames: {
        "balance.currency": this.$t("cashflow.definition.balanceColumnNames.currency"),
        //"balance.bankCode": "Bank code",
        "balance.accountNumber": this.$t("cashflow.definition.balanceColumnNames.accountNumber"),
        "balance.accountName": this.$t("cashflow.definition.balanceColumnNames.accountName"),
        "balance.accountOwner": this.$t("cashflow.definition.balanceColumnNames.accountOwner"),
        "balance.accountType": this.$t("cashflow.definition.balanceColumnNames.accountType")
      },
      cashEventColumns: [],
      cashEventColumnNames: {
        "cashEvent.currency": this.$t("cashflow.definition.cashEventColumnNames.currency"),
        "cashEvent.accountCurrency": this.$t("cashflow.definition.cashEventColumnNames.accountCurrency"),
        "cashEvent.status": this.$t("cashflow.definition.cashEventColumnNames.status"),
        "cashEvent.account": this.$t("cashflow.definition.cashEventColumnNames.account"),
        "cashEvent.counterparty": this.$t("cashflow.definition.cashEventColumnNames.counterparty"),
        "cashEvent.type": this.$t("cashflow.definition.cashEventColumnNames.type"),
        "cashEvent.category": this.$t("cashflow.definition.cashEventColumnNames.category"),
        "cashEvent.subcategory": this.$t("cashflow.definition.cashEventColumnNames.subcategory"),
        "cashEvent.source": this.$t("cashflow.definition.cashEventColumnNames.source"),
        "cashEvent.accountOwner": this.$t("cashflow.definition.cashEventColumnNames.accountOwner"),
        "cashEvent.accountType": this.$t("cashflow.definition.cashEventColumnNames.accountType")
      }
    }
  },
  computed: {
    ...mapGetters({
      supportedPartyRoleIds: "accountOwnerships/supportedPartyRoleIds",
      columns: "cashflowReportColumns/cashflowReportColumns",
      currencies: "currencies/currenciesOptions",
      accounts: "banking/accounts",
      cashEventFieldValues: "cashEventFieldDistincts/fieldValues",
      operationQueries: "operationQueries/operationQueries",
      cashEventQueries: "cashEventQueries/cashEventQueries",
      parties: "parties/parties",
      users: "users/users",
      cashEffects: "cashEffects/cashEffects"
    }),
    cashEffectOptions() {
      return this.cashEffects.map(cashEffect => ({name: `${cashEffect.category}/${cashEffect.name}`, id: cashEffect.id}))
    },
    userOptions() {
      let result = [{id: "public", name: "Everyone"}, ...this.users.map(user => ({id: user.id, name: user.username}))]
      return result;
    },
    currencyOptions() {
      let result = [...this.currencies];
      return result;
    },
    accountsForFilter() {
      return this.accounts.filter(acc => acc.internal)
    },
    operationQueryOptions() {
      return this.operationQueries.map(query => ({
        id: query.name,
        name: query.name == null ? "No-name" : query.name
      }))
    },
    cashEventQueryOptions() {
      return this.cashEventQueries.map(query => ({
        id: query.name,
        name: query.name
      }))
    },
    partyOptions() {
      return this.parties.filter(
          party => {
            return party.partyRoles.some(partyRole => {
              return this.supportedPartyRoleIds.includes(partyRole.partyRoleTypeId) &&
                  partyRole.attributeComponent.values.AccountOwner.InternalAccountOwner == true;
            })
          }
      ).map(party => ({
        name: party.displayName,
        id: party.partyRoles.filter(partyRole => this.supportedPartyRoleIds.includes(partyRole.partyRoleTypeId))[0].id
      }))
    }
  },
  async mounted() {
    await this.$store.dispatch("cashflowReportColumns/refreshCashflowReportColumns");

    this.balanceColumns = this.balanceColumns.filter(col => !this.internalDefinition.balanceColumns.includes(col));
    this.cashEventColumns = this.columns.filter(col => !this.internalDefinition.columns.includes(col));
  },
  watch: {
    definition(newVal) {
      this.internalDefinition = this.$_.cloneDeep(newVal);
      if (typeof this.internalDefinition.cashflowFilter == 'undefined') {
        this.internalDefinition.cashflowFilter = {};
        this.internalDefinition.savedQueryNames = [];
        this.internalDefinition.cashflowFilter.criteriaMap = {
          ACCOUNT: [],
          CURRENCY: [],
          EVENT_SOURCE: [],
          EVENT_TYPE: []
        };
      }
    },
    'internalDefinition.convertToReportCurrency': {
      handler(newVal) {
        if (!newVal) {
          this.internalDefinition.reportCurrency = "CZK";
        }
      }
    }
  },
  methods: {
    emitSave() {
      this.$emit('save', this.internalDefinition);
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/global";

.columns-container {

  background-color: var(--input-background-color);
  border: 1px solid var(--input-border-color);
  border-radius: 5px;
  margin: 10px;
  padding: 5px;
}

.columns-item {
  background-color: var(--input-list-background-color);
  color: var(--input-active-font-color);
  border-radius: 5px;
  padding: 5px;
  margin: 5px;
  user-select: none;
  cursor: grab;
}

.drop-area {
  min-height: 20px;
}

select {
  width: 100%;
  height: 90px;
  background-color: var(--input-list-background-color);
  border: 1px solid var(--input-border-color);
  color: var(--input-active-font-color);
}

select option:checked {
  background-color: $openRed !important;
}

.selection-grid {
  display: grid;
  grid-template: 1fr / 1fr min-content 1fr;
  grid-gap: 10px;
}

.selection-arrows {
  display: flex;
  flex-flow: column;
  justify-items: center;
  justify-content: center;
  align-content: center;
  align-items: center;
}
</style>
